/**
 * The words of "Pass to another agent" (plan-021 WAPP-PASS; §2.12's panel table and design §33.14, verbatim in English
 * and Greek, catalog `voiceAgentPass`). Every sentence the panel and the lifecycle confirmations draw from a fact is made
 * here, so the panel holds no copy decision of its own and a suite can name each one.
 *
 * Copy beyond §2.12 / §33.14 (for the owner's copy review at AP-3): `panel.read_failed`, `medium.phone`,
 * `medium.web_chat`, `picker.empty`.
 */
import { ApiError } from "@wetalk/api-client";
import {
  SINGLE_LINE_WITHOUT_CONTROL_STRING,
  VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT,
  VOICE_AGENT_PASS_TARGET_LIMIT,
  VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT,
  type VoiceAgentAudience,
} from "@wetalk/domain";

import { apiErrorText } from "../../../../api/api-error.js";
import { languageNameOf } from "../../../../api/conversation-view.js";
import {
  VoiceAgentPassRefusedError,
  type PassCandidateView,
  type PassTargetState,
  type PassTargetWarningMedium,
  type PassTargetWarningView,
} from "../../../../api/slice/voice-agent-pass.js";
import { portalLocale, portalText } from "../../../../i18n/portal.js";

/** "A, B and C" in the portal's language. */
export function namesText(names: readonly string[]): string {
  return new Intl.ListFormat(portalLocale() === "el" ? "el-GR" : "en-GB", { style: "long", type: "conjunction" }).format(names);
}

/** The line under a target that is not live now, or `null` for a live one. */
export function targetStateTextOf(state: PassTargetState): string | null {
  switch (state) {
    case "live":
      return null;
    case "not_live":
      return portalText("voiceAgentPass", "state.not_live");
    case "paused":
      return portalText("voiceAgentPass", "state.paused");
    case "unavailable":
      return portalText("voiceAgentPass", "state.unavailable");
  }
}

function mediumWordOf(medium: PassTargetWarningMedium): string {
  return medium === "phone" ? portalText("voiceAgentPass", "medium.phone") : portalText("voiceAgentPass", "medium.web_chat");
}

/**
 * A warning in words, or `null` for one the state line already says (`pass_target_not_live` — the target's state is
 * drawn above it). `sourceLanguage` is the source's live languages: a warning naming every one of them is "speaks none".
 */
export function warningTextOf(warning: PassTargetWarningView, sourceLanguage: readonly string[]): string | null {
  switch (warning.code) {
    case "pass_target_not_live":
      return null;
    case "pass_target_no_shared_language": {
      const none = sourceLanguage.length > 0 && sourceLanguage.every((code) => warning.languageCode.includes(code));
      if (none || warning.languageCode.length === 0) return portalText("voiceAgentPass", "warning.no_shared_language");
      return portalText("voiceAgentPass", "warning.some_languages", { languages: namesText(warning.languageCode.map(languageNameOf)) });
    }
    case "pass_target_no_shared_medium":
      return portalText("voiceAgentPass", "warning.no_shared_medium", { media: namesText(warning.medium.map(mediumWordOf)) });
  }
}

/** Why a candidate cannot be added, or `null` when it can. An internal source reads the wall's own sentence. */
export function candidateRefusalTextOf(candidate: PassCandidateView, sourceAudience: VoiceAgentAudience | null): string | null {
  if (candidate.mayReceive) return null;
  if (candidate.refusal === "pass_target_audience_crossed") {
    return sourceAudience === "external"
      ? portalText("voiceAgentPass", "candidate.disabled_audience")
      : portalText("voiceAgentPass", "refusal.pass_target_audience_crossed");
  }
  return portalText("voiceAgentPass", "candidate.disabled_cannot_receive");
}

/** The limit sentence, for the "Add an agent" button once the list is full. */
export function targetLimitText(limit: number = VOICE_AGENT_PASS_TARGET_LIMIT): string {
  return portalText("voiceAgentPass", "refusal.pass_target_limit_exceeded", { limit });
}

/** "When to pass" checked before sending, as the API checks it: 1–240 characters on one line. */
export function whenProblemOf(when: string): string | null {
  const trimmed = when.trim();
  return trimmed.length === 0 || trimmed.length > VOICE_AGENT_PASS_WHEN_CHARACTER_LIMIT || !SINGLE_LINE_WITHOUT_CONTROL_STRING.test(trimmed)
    ? portalText("voiceAgentPass", "refusal.when_length")
    : null;
}

/** The note checked before sending: at most 240 characters on one line. */
export function noteProblemOf(note: string): string | null {
  const trimmed = note.trim();
  return trimmed.length > VOICE_AGENT_PASS_NOTE_CHARACTER_LIMIT || !SINGLE_LINE_WITHOUT_CONTROL_STRING.test(trimmed)
    ? portalText("voiceAgentPass", "refusal.note_length")
    : null;
}

/** A refused save in words: the sentence, and the entry of the sent list it is about (`null`: the whole list). */
export interface PassTargetRefusalText {
  readonly sentence: string;
  readonly index: number | null;
}

const ENTRY_REFUSAL = [
  "pass_target_self",
  "pass_target_duplicate",
  "pass_target_not_found",
  "pass_target_archived",
  "pass_target_cannot_receive",
  "pass_target_audience_crossed",
] as const;

/**
 * A refused save, in the portal's language from its code and detail — every refusal of `PUT …/pass-targets`, in the
 * API's order: `validation_failed` (`when` and `note` by their own sentences, any other field in the API's words),
 * `voice_agent_not_found` (the API's words), `agent_version_not_ready`, `pass_source_cannot_pass`,
 * `pass_target_limit_exceeded` (with the limit) and the six per-entry refusals (with the entry). `409
 * live_version_seq_stale` is not here: the panel re-reads and says `refusal.stale`.
 */
export function saveRefusalTextOf(error: unknown): PassTargetRefusalText {
  const index = error instanceof VoiceAgentPassRefusedError ? error.index : null;
  if (error instanceof ApiError) {
    if (error.code === "validation_failed" && error.field === "when") return { sentence: portalText("voiceAgentPass", "refusal.when_length"), index };
    if (error.code === "validation_failed" && error.field === "note") return { sentence: portalText("voiceAgentPass", "refusal.note_length"), index };
    if (error.code === "agent_version_not_ready") return { sentence: portalText("voiceAgentPass", "refusal.not_ready"), index: null };
    if (error.code === "pass_source_cannot_pass") return { sentence: portalText("voiceAgentPass", "refusal.pass_source_cannot_pass"), index: null };
    if (error.code === "pass_target_limit_exceeded") {
      const limit = error instanceof VoiceAgentPassRefusedError && error.limit !== null ? error.limit : VOICE_AGENT_PASS_TARGET_LIMIT;
      return { sentence: targetLimitText(limit), index: null };
    }
    const entry = ENTRY_REFUSAL.find((one) => one === error.code);
    if (entry !== undefined) return { sentence: portalText("voiceAgentPass", `refusal.${entry}`), index };
  }
  // Anything else — `voice_agent_not_found`, a `validation_failed` about another field, a fault — in the API's words.
  return { sentence: apiErrorText(error, portalText("operations", "nothing_was_changed_c60d5d2a")), index };
}

/** "These agents pass conversations to {name}: A and B. Afterwards …" — or `null` when none does. */
export function referrersTextOf(name: string, referrer: readonly { readonly displayName: string }[]): string | null {
  if (referrer.length === 0) return null;
  return portalText("voiceAgentPass", "lifecycle.referrers", {
    count: referrer.length,
    name,
    voice_agents: namesText(referrer.map((one) => one.displayName)),
  });
}
